<template>
  <div class="h-full flex flex-col overflow-hidden">

    <div v-if="loading" class="flex-1 flex items-center justify-center text-gray-600 text-xs">
      Loading history…
    </div>

    <div v-else-if="!commits.length" class="flex-1 flex items-center justify-center text-gray-600 text-xs">
      No commit history
    </div>

    <div v-else class="flex-1 overflow-y-auto">
      <div
        v-for="entry in commits"
        :key="entry.hash"
        class="flex items-start gap-2 px-3 py-2 border-b border-gray-800 hover:bg-gray-800/60 cursor-pointer group"
        @click="$emit('select', entry)"
      >
        <!-- Author initial badge -->
        <div class="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center text-[9px] font-semibold text-gray-300 shrink-0 mt-0.5 uppercase select-none">
          {{ entry.author_name.charAt(0) }}
        </div>

        <div class="flex-1 min-w-0">
          <div class="text-xs text-gray-200 leading-snug">{{ entry.message }}</div>
          <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <code class="text-[10px] font-mono text-blue-400">{{ entry.hash.slice(0, 7) }}</code>
            <span class="text-[10px] text-gray-700">·</span>
            <span class="text-[10px] text-gray-500 truncate max-w-24">{{ entry.author_name }}</span>
            <span class="text-[10px] text-gray-700">·</span>
            <time class="text-[10px] text-gray-600" :title="entry.date">{{ relativeDate(entry.date) }}</time>
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="p-2 text-center">
        <button
          class="text-xs text-gray-500 hover:text-gray-300 px-3 py-1 rounded border border-gray-700 hover:border-gray-500 transition-colors"
          @click="$emit('load-more')"
        >
          Load more
        </button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import type { GitCommit } from '@/services/gitService'

defineProps<{
  commits:  GitCommit[]
  loading?: boolean
  hasMore?: boolean
}>()

defineEmits<{
  (e: 'select',    commit: GitCommit): void
  (e: 'load-more'): void
}>()

function relativeDate(dateStr: string): string {
  try {
    const diff  = Date.now() - new Date(dateStr).getTime()
    const mins  = Math.floor(diff / 60_000)
    const hours = Math.floor(diff / 3_600_000)
    const days  = Math.floor(diff / 86_400_000)
    if (mins  < 1)   return 'just now'
    if (mins  < 60)  return `${mins}m ago`
    if (hours < 24)  return `${hours}h ago`
    if (days  < 7)   return `${days}d ago`
    return new Date(dateStr).toLocaleDateString('en', { month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}
</script>
